import { supabase } from '../config/supabaseClient';
import { withErrorHandling } from '../utils/errorHandler';
import { createAppError, ErrorCategory } from '../utils/errorHandler';
import { ShortenedUrl, UrlShorteningRequest, UrlClickAnalytics } from '../types/urlShortener';

/**
 * Service for URL shortening features
 */
export class UrlShortenerService {
  /**
   * Validate user authentication
   */
  private async validateAuthentication(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw createAppError(
        'Not authenticated',
        ErrorCategory.AUTH,
        401,
        'You must be logged in to use this feature'
      );
    }
    return session.user.id;
  }

  /**
   * Generate a unique short code for a URL
   * Uses a simple base62 encoding of a timestamp + random number
   */
  private generateShortCode(): string {
    const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const BASE = ALPHABET.length;
    
    // Combine timestamp with random number to ensure uniqueness
    const now = Date.now();
    const rand = Math.floor(Math.random() * 10000);
    let num = now + rand;
    
    // Convert to base62 string
    let code = '';
    while (num > 0) {
      code = ALPHABET[num % BASE] + code;
      num = Math.floor(num / BASE);
    }
    
    // Ensure it's at least 5 chars and at most 8 chars
    if (code.length < 5) {
      for (let i = code.length; i < 5; i++) {
        code += ALPHABET[Math.floor(Math.random() * BASE)];
      }
    }
    return code.slice(0, 8);
  }

  /**
   * Create a shortened URL
   */
  async shortenUrl(request: UrlShorteningRequest): Promise<ShortenedUrl> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Generate a short code if not provided
        const shortCode = request.custom_code || this.generateShortCode();
        
        // Validate URL
        const urlPattern = /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/;
        if (!urlPattern.test(request.original_url)) {
          throw createAppError(
            'Invalid URL', 
            ErrorCategory.VALIDATION, 
            400, 
            'The provided URL is invalid'
          );
        }
        
        // Check if custom code is already used
        if (request.custom_code) {
          const { data: existingUrl } = await supabase
            .from('shortened_urls')
            .select('id')
            .eq('short_code', request.custom_code)
            .maybeSingle();
            
          if (existingUrl) {
            throw createAppError(
              'Custom code already used', 
              ErrorCategory.VALIDATION, 
              400, 
              'This custom code is already in use'
            );
          }
        }
        
        // Create the shortened URL
        const { data, error } = await supabase
          .from('shortened_urls')
          .insert({
            user_id: userId,
            original_url: request.original_url,
            short_code: shortCode,
            title: request.title || null,
            created_for_post_id: request.created_for_post_id || null
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating shortened URL:', error);
          throw createAppError(
            'Failed to create shortened URL', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error creating your shortened URL',
            { error }
          );
        }

        return data;
      } catch (err: any) {
        console.error('Error in shortenUrl:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to shorten URL', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error creating your shortened URL',
          { originalError: err }
        );
      }
    }, { service: 'UrlShortenerService', method: 'shortenUrl' });
  }

  /**
   * Get all shortened URLs for the current user
   */
  async getUserShortenedUrls(): Promise<ShortenedUrl[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        const { data, error } = await supabase
          .from('shortened_urls')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching shortened URLs:', error);
          throw createAppError(
            'Failed to fetch shortened URLs', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your shortened URLs',
            { error }
          );
        }

        return data || [];
      } catch (err: any) {
        console.error('Error in getUserShortenedUrls:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch shortened URLs', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading your shortened URLs',
          { originalError: err }
        );
      }
    }, { service: 'UrlShortenerService', method: 'getUserShortenedUrls' });
  }

  /**
   * Get a shortened URL by its short code
   * This is used by the redirection service
   */
  async getShortenedUrlByCode(shortCode: string): Promise<ShortenedUrl | null> {
    return withErrorHandling(async () => {
      try {
        const { data, error } = await supabase
          .from('shortened_urls')
          .select('*')
          .eq('short_code', shortCode)
          .eq('is_active', true)
          .maybeSingle();

        if (error) {
          console.error('Error fetching shortened URL by code:', error);
          throw createAppError(
            'Failed to fetch shortened URL', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading the shortened URL',
            { error }
          );
        }

        return data;
      } catch (err: any) {
        console.error('Error in getShortenedUrlByCode:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch shortened URL by code', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading the shortened URL',
          { originalError: err }
        );
      }
    }, { service: 'UrlShortenerService', method: 'getShortenedUrlByCode' });
  }

  /**
   * Get analytics for a shortened URL
   */
  async getUrlAnalytics(shortCode: string): Promise<UrlClickAnalytics> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Get the URL data
        const { data, error } = await supabase
          .from('shortened_urls')
          .select('*')
          .eq('short_code', shortCode)
          .eq('user_id', userId)
          .single();

        if (error || !data) {
          console.error('Error fetching URL analytics:', error);
          throw createAppError(
            'Failed to fetch URL analytics', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading the URL analytics',
            { error }
          );
        }

        // In a real implementation, we would fetch detailed click history
        // For now, we'll return basic analytics
        return {
          total_clicks: data.clicks,
          click_history: [
            {
              date: new Date().toISOString().split('T')[0],
              count: data.clicks
            }
          ]
        };
      } catch (err: any) {
        console.error('Error in getUrlAnalytics:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch URL analytics', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading the URL analytics',
          { originalError: err }
        );
      }
    }, { service: 'UrlShortenerService', method: 'getUrlAnalytics' });
  }

  /**
   * Deactivate a shortened URL
   */
  async deactivateUrl(shortCode: string): Promise<void> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        const { error } = await supabase
          .from('shortened_urls')
          .update({ is_active: false })
          .eq('short_code', shortCode)
          .eq('user_id', userId);

        if (error) {
          console.error('Error deactivating shortened URL:', error);
          throw createAppError(
            'Failed to deactivate URL', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error deactivating the shortened URL',
            { error }
          );
        }
      } catch (err: any) {
        console.error('Error in deactivateUrl:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to deactivate URL', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error deactivating your shortened URL',
          { originalError: err }
        );
      }
    }, { service: 'UrlShortenerService', method: 'deactivateUrl' });
  }

  /**
   * Get the full shortened URL including domain
   * @param shortCode The short code or full ShortenedUrl object
   * @returns The full shortened URL
   */
  getFullShortenedUrl(shortCode: string | ShortenedUrl): string {
    // If object passed, extract the code
    const code = typeof shortCode === 'string' ? shortCode : shortCode.short_code;
    
    // Use the app's domain from environment variable or fallback
    const appDomain = process.env.REACT_APP_DOMAIN || window.location.host;
    
    // Construct the URL with https protocol
    return `https://${appDomain}/s/${code}`;
  }
}

// Export a singleton instance
export const urlShortenerService = new UrlShortenerService();
